import React from "react";
import styled from "styled-components/macro";
import { useTranslation } from "react-i18next";
import { Redirect, Link, useLocation } from "react-router-dom";
import { GameScreen, ScreenAreaRenderProps } from "../components/GameScreen";
import { useAuth } from "../../data/auth";
import { TextInput } from "../components/TextInput";
import { useFormInput } from "../../hooks/useFormInput";
import { Button } from "../components/Button";
import { useBrand } from "../../data/brand";
import { logger } from "../../logger";
import { GameHeader } from "../components/GameHeader";
import { Markdown } from "../components/Markdown";

const Container = styled.div`
  flex: 1;
  background-color: ${(props) => props.theme.colors.background};
  overflow-y: auto;
`;
const MarkdownContent = styled(Markdown)`
  margin: 16px 16px 0 16px;
`;
const FormContainer = styled.div`
  padding: 16px 16px 0 16px;
  display: flex;
  flex-direction: column;
  background-color: ${(props) => props.theme.colors.background};
`;
const FormTextInput = styled(TextInput)`
  margin-bottom: 16px;
`;
const SubmitContainer = styled.div`
  padding: 0 16px;
`;
const AuthButton = styled(Button)`
  margin-bottom: 8px;
`;
const AuthLinkContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 8px 0;
`;
const AuthLink = styled(Link)`
  text-decoration: underline;

  &:hover {
    text-decoration: none;
  }
`;

export const ForgottenPassword = () => {
  const { t } = useTranslation();
  const {
    brandDetails: { allowSignUp, domain, landingPageContent },
  } = useBrand();
  const { isSignedIn, performPasswordReset } = useAuth();
  const location = useLocation();

  const [email, handleEmailChange] = useFormInput();

  const handleFormSubmit = React.useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const trimmedEmail = email.trim();
      if (trimmedEmail.length === 0) {
        alert(t("auth.formError"));
        return;
      }

      try {
        await performPasswordReset({
          email: trimmedEmail,
          continueUrl: `https://${domain}/login${location.search}`,
        });
        alert(t("auth.forgottenPasswordSent"));
        return;
      } catch (e) {
        logger.warn("Error performing auth", e);

        if (e.code === "auth/invalid-email") {
          alert(t("auth.invalidEmailError"));
        } else {
          alert(t("auth.authError"));
        }
      }
    },
    [domain, email, location.search, performPasswordReset, t]
  );

  const renderHeader = React.useCallback(
    ({ toggleMenu }: ScreenAreaRenderProps) => {
      return (
        <GameHeader
          toggleMenu={toggleMenu}
          title={t("auth.forgottenPasswordTitle")}
        />
      );
    },
    [t]
  );

  const renderBody = React.useCallback(() => {
    return (
      <Container>
        {!!landingPageContent?.auth?.forgottenPassword && (
          <MarkdownContent source={landingPageContent.auth.forgottenPassword} />
        )}

        <form onSubmit={handleFormSubmit}>
          <FormContainer>
            <FormTextInput
              placeholder={t("auth.emailPlaceholder")}
              value={email}
              onChange={handleEmailChange}
            />
          </FormContainer>

          <SubmitContainer>
            <AuthButton type="submit">
              {t("auth.submitForgottenPassword")}
            </AuthButton>
            <AuthLinkContainer>
              {allowSignUp && (
                <>
                  <AuthLink to={`/signUp${location.search}`}>
                    {t("auth.signUp")}
                  </AuthLink>
                  &nbsp;|&nbsp;
                </>
              )}
              <AuthLink to={`/login${location.search}`}>
                {t("auth.login")}
              </AuthLink>
            </AuthLinkContainer>
          </SubmitContainer>
        </form>
      </Container>
    );
  }, [
    allowSignUp,
    email,
    handleEmailChange,
    handleFormSubmit,
    landingPageContent,
    location.search,
    t,
  ]);

  if (isSignedIn) {
    return <Redirect to="/" />;
  }

  return <GameScreen renderHeader={renderHeader} renderBody={renderBody} />;
};
